import { type ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { customerSignatureApi, type CustomerSignatureView, type SignatureKind } from '../api/customerSignature'
import { errMsg } from '../lib/financeFormat'
import { SignaturePad } from './SignaturePad'
import { Button, Callout, Card, CardContent, CardHeader } from './ui'

const LABEL: Record<SignatureKind, string> = { firma: 'Firma', timbro: 'Timbro' }

/**
 * Firma e timbro del cliente, riusati in tutte le sue pratiche e nei moduli generati dal CRM. Si disegnano sullo
 * schermo o si caricano da una foto su foglio bianco (il server toglie lo sfondo); il PNG va nella cartella Drive
 * del cliente. La firma serve per far uscire la pratica dalla bozza; il timbro, se manca, si genera dai dati aziendali.
 */
export function CustomerSignatureCard({ customerId, required, onSaved }: {
  customerId: number
  required: boolean
  onSaved?: () => void
}) {
  const [data, setData] = useState<CustomerSignatureView | null>(null)
  const [hidden, setHidden] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [drawing, setDrawing] = useState<SignatureKind | null>(null)
  const [drawn, setDrawn] = useState('')
  const [version, setVersion] = useState(0)
  const fileRef = useRef<HTMLInputElement>(null)
  const [fileKind, setFileKind] = useState<SignatureKind>('firma')

  const load = useCallback(() => {
    customerSignatureApi.get(customerId)
      .then(setData)
      .catch(e => { if (/403|404|forbidden|not found/i.test(errMsg(e, ''))) setHidden(true); else setError(errMsg(e, 'Firma non disponibile.')) })
  }, [customerId])

  useEffect(() => { load() }, [load])

  const run = async (key: string, action: () => Promise<unknown>) => {
    setBusy(key)
    setError('')
    try {
      await action()
      setDrawing(null)
      setDrawn('')
      setVersion(v => v + 1)
      load()
      onSaved?.()
    } catch (e) {
      setError(errMsg(e, 'Salvataggio non riuscito.'))
    } finally {
      setBusy(null)
    }
  }

  const pickPhoto = (kind: SignatureKind) => {
    setFileKind(kind)
    fileRef.current?.click()
  }

  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (file) void run(`foto-${fileKind}`, () => customerSignatureApi.uploadPhoto(customerId, fileKind, file))
  }

  if (hidden || !data) return error ? <Callout tone="error">{error}</Callout> : null

  const slot = (kind: SignatureKind) => {
    const image = data[kind]
    return (
      <div className="flex-grow-1" style={{ minWidth: 220 }}>
        <p className="md-typescale-title-small mb-2">{LABEL[kind]}</p>
        <div
          className="d-flex align-items-center justify-content-center mb-2"
          style={{ height: 110, border: '1px dashed var(--md-sys-color-outline-variant)', borderRadius: 8, background: '#fff' }}
        >
          {image
            ? <img src={`${image.url}&v=${version}`} alt={`${LABEL[kind]} del cliente`} style={{ maxHeight: 100, maxWidth: '100%' }} />
            : <span className="text-muted md-typescale-body-small">{kind === 'firma' ? 'Nessuna firma' : 'Nessun timbro'}</span>}
        </div>
        <div className="d-flex flex-wrap gap-2">
          <Button variant="outlined" size="sm" icon="draw" disabled={!!busy} onClick={() => { setDrawing(kind); setDrawn('') }}>
            Disegna
          </Button>
          <Button variant="outlined" size="sm" icon="photo_camera" loading={busy === `foto-${kind}`} disabled={!!busy} onClick={() => pickPhoto(kind)}>
            Carica foto
          </Button>
          {kind === 'timbro' && data.canGenerateStamp && (
            <Button
              variant="text" size="sm" icon="auto_awesome" loading={busy === 'genera'} disabled={!!busy}
              onClick={() => void run('genera', () => customerSignatureApi.generateStamp(customerId))}
            >
              {image ? 'Rigenera' : 'Genera'}
            </Button>
          )}
        </div>
        {kind === 'timbro' && !data.canGenerateStamp && (
          <p className="text-muted md-typescale-body-small mt-2 mb-0">
            Per generare il timbro dai dati servono la partita IVA nella scheda cliente, oppure il codice fiscale di un'azienda, ditta o condominio.{' '}
            <Link to={`/customers/${customerId}/edit`}>Completa la scheda cliente</Link>
          </p>
        )}
      </div>
    )
  }

  return (
    <Card variant="outlined" padding="none">
      <CardHeader
        icon="signature"
        title="Firma e timbro del cliente"
        subtitle="Salvati nella cartella Drive del cliente e applicati ai moduli da firmare. Foto su foglio bianco: lo sfondo si toglie da solo."
      />
      <CardContent>
        {error && <Callout tone="error" className="mb-3">{error}</Callout>}
        {required && !data.firma && (
          <Callout tone="warning" className="mb-3">Manca la firma del cliente: senza firma la pratica resta in bozza.</Callout>
        )}
        <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={onFile} />
        <div className="d-flex flex-wrap gap-4">
          {slot('firma')}
          {slot('timbro')}
        </div>
        {drawing && (
          <div className="mt-3">
            <SignaturePad
              id={`customer-${drawing}-pad`}
              label={drawing === 'firma' ? 'Firma qui' : 'Disegna il timbro'}
              error={undefined}
              value={drawn}
              disabled={!!busy}
              onChange={setDrawn}
            />
            <div className="d-flex gap-2 mt-2">
              <Button
                variant="filled" icon="save" loading={busy === 'disegno'} disabled={!drawn || !!busy}
                onClick={() => void run('disegno', () => customerSignatureApi.saveDrawing(customerId, drawing, drawn))}
              >
                Salva {drawing}
              </Button>
              <Button variant="text" disabled={!!busy} onClick={() => setDrawing(null)}>Annulla</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
